import { useState, useEffect } from 'react';
import { sessionManager } from '../utils/sessionManager';
import { chatService } from '../api/chatService';
import { Message } from '../components/ChatInterface';

export function useSessionState() {
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [userName, setUserName] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string>("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [isProfileUpdated, setIsProfileUpdated] = useState<boolean>(false);

  useEffect(() => {
    const initSession = async () => {
      const storedState = sessionManager.getStoredSessionState();
      if (storedState?.sessionId) {
        try {
          const historyResponse = await chatService.getSessionHistory(storedState.sessionId);
          const restoredMessages = historyResponse.history.map(historyMsg => ({
            id: historyMsg.id.toString(),
            type: historyMsg.role,
            content: historyMsg.content,
            timestamp: new Date(historyMsg.timestamp),
            intent: historyMsg.intent
          }));
          localStorage.setItem('isProfileUpdated', storedState?.isProfileUpdated?.toString() || "false");
          setHasSession(true);
          setUserName(storedState.userName);
          setSessionId(historyResponse.session_id);
          setMessages(restoredMessages);
          setIsProfileUpdated(storedState?.isProfileUpdated || false);
          chatService.saveSessionId(historyResponse.session_id);
        } catch (error) {
          console.warn('Failed to restore session:', error);
          sessionManager.clearStoredSessionState();
        }
      }
      setIsInitialized(true);
    };

    initSession();
  }, []);

  const createNewSession = async (name: string) => {
    try {
      const response = await chatService.createSession(name);
      if (response.session_id) {
        setHasSession(true);
        setUserName(name);
        setSessionId(response.session_id);
        sessionManager.saveSessionState(response.session_id, name);
        chatService.saveSessionId(response.session_id);
      }
    } catch (error) {
      console.error('Failed to create session:', error);
      throw error;
    }
  };

  const clearSession = () => {
    chatService.clearSession();
    sessionManager.clearStoredSessionState();
    setHasSession(false);
    setUserName(null);
    setSessionId("");
    setMessages([]);
    setIsInitialized(true);
  };

  const addMessage = (message: Omit<Message, 'id' | 'timestamp'>) => {
    const newMessage: Message = {
      ...message,
      id: Date.now().toString() + Math.random(),
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, newMessage]);
    return newMessage.id;
  };

  const updateMessage = (id: string, updates: Partial<Message>) => {
    setMessages(prev => prev.map(msg =>
      msg.id === id ? { ...msg, ...updates } : msg
    ));
  };
  const updateIsProfileUpdated = (isProfileUpdated: boolean) => {
    setIsProfileUpdated(isProfileUpdated);
    localStorage.setItem("isProfileUpdated", isProfileUpdated.toString());
    sessionManager.updateSessionState(sessionId, userName, isProfileUpdated);
  };
  const updateUserName = (userName: string | null) => {
    sessionManager.updateSessionState(sessionId, userName);
    setUserName(userName);
  };

  return {
    isInitialized,
    hasSession,
    userName,
    sessionId,
    messages,
    isProfileUpdated,
    addMessage,
    updateMessage,
    updateIsProfileUpdated,
    createNewSession,
    clearSession,
    setMessages,
    updateUserName
  };
}